import NoteSection from '@/src/components/desktop/srp/flightResults/flightDetails/noteSection'
import { MiniRuleData } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { isEmpty } from '@/src/utils/generalUtils'
import { Box, Card, Tab, Tabs, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'
import RefundInfo from '../flightDetails/refundInfo'
import { BaggageSection } from './components/BaggageSection'
import { FareRulesSection } from './components/FareRulesSection'
import { LoadingSkeletons } from './components/LoadingSkeletons'
import { NoDataSection } from './components/NoDataSection'

interface TabItem {
    label: string
    key: string
}

interface ProcessedBaggageData {
    checkIn: string | null
    cabin: string | null
}

interface FareRuleDetailsProps {
    processedBaggageData: ProcessedBaggageData | null
    processedTabs: TabItem[]
    activeTab: string
    isLoading: boolean
    error: string | null
    onTabChange: (tab: string) => void
    t: TranslationFunction<'common'>
    miniruleData: MiniRuleData | undefined
    miniRuleLoading: boolean
    miniRuleerror: string | null
    showBaggage?: boolean
    formatCurrency: (price: number) => string
    // Retry props
    retryCount?: number
    maxRetries?: number
    onRetryBaggage?: () => void
}

const FareRuleDetails: React.FC<FareRuleDetailsProps> = ({
    processedBaggageData,
    processedTabs,
    activeTab,
    isLoading,
    error,
    onTabChange,
    t,
    miniruleData,
    miniRuleLoading,
    miniRuleerror,
    showBaggage,
    formatCurrency,
    retryCount = 0,
    maxRetries = 2,
    onRetryBaggage
}) => {
    const tFlightDetails = useTranslations('srpFlightDetails')
    const showFareRules = miniruleData?.MR === 'Y' && miniruleData?.Refund === 'Y'
    const noteText = miniruleData?.note
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ my: 2, fontWeight: 600, fontSize: 16 }}>
                {t('fareRules')}
            </Typography>
            <Card sx={{ border: '1px solid #e5e7eb', padding: '8px 16px 16px 16px' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => onTabChange(newValue)}
                    sx={{
                        mb: 2,
                        '& .MuiTabs-indicator': {
                            backgroundColor: 'brand.primary'
                        }
                    }}
                >
                    {processedTabs.map(tab => (
                        <Tab
                            key={tab.key}
                            label={tab.label}
                            value={tab.label}
                            sx={{
                                textTransform: 'none',
                                fontWeight: activeTab === tab.label ? 'bold' : 'normal',
                                color: activeTab === tab.label ? 'brand.primary' : 'text.secondary'
                            }}
                        />
                    ))}
                </Tabs>
                <Card
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    {showBaggage && (
                        <BaggageSection
                            processedBaggageData={processedBaggageData}
                            isLoading={isLoading}
                            retryCount={retryCount}
                            maxRetries={maxRetries}
                            onRetryBaggage={onRetryBaggage}
                        />
                    )}
                    {miniRuleLoading ? (
                        <LoadingSkeletons />
                    ) : isEmpty(miniruleData) ? (
                        <NoDataSection
                            title={t('fareRules')}
                            message={tFlightDetails('noFareRuleData')}
                        />
                    ) : showFareRules ? (
                        <FareRulesSection
                            miniruleData={miniruleData}
                            formatCurrency={formatCurrency}
                            tFlightDetails={tFlightDetails}
                        />
                    ) : miniruleData?.MR === 'N' || miniruleData?.Refund === 'N' ? (
                        <RefundInfo />
                    ) : (
                        <NoDataSection
                            title={t('fareRules')}
                            message={tFlightDetails('noFareRulesAvailable')}
                        />
                    )}
                </Card>
                {error && (
                    <Typography color="error" sx={{ my: 2, fontWeight: 500 }}>
                        {error}
                    </Typography>
                )}
                {miniRuleerror && (
                    <Typography color="error" p={2}>
                        {tFlightDetails('fareRulesError')}
                    </Typography>
                )}
                {noteText && <NoteSection />}
            </Card>
        </Box>
    )
}

export { FareRuleDetails }
export type { FareRuleDetailsProps, ProcessedBaggageData, TabItem }
